// ============================================================
// 紧固件计算器 - 共享定义
// 服务端（动态路由）和客户端（FastenerCalculator组件）均需要引用
// 此文件没有 "use client"，因此在 Server Component 中可调用函数
// ============================================================

export type CalculatorId =
  | "unit-converter"
  | "torque-metric"
  | "tap-drill"
  | "hardness"
  | "tension-check"
  | "torque-unified"
  | "shear-check"
  | "min-engagement"
  | "bolt-length"
  | "slip-factor";

export type CalcLang = "zh" | "en";

export interface CalculatorMeta {
  id: CalculatorId;
  title: string;
  desc: string;
}

export const CALCULATOR_LIST: CalculatorMeta[] = [
  { id: "unit-converter", title: "公英制单位换算器", desc: "长度英寸 / 毫米快速换算" },
  { id: "torque-metric", title: "螺栓拧紧扭矩计算器（公制）", desc: "根据规格等级计算装配扭矩" },
  { id: "tap-drill", title: "螺纹底孔直径计算器", desc: "公制粗牙 / 细牙攻丝底孔推荐尺寸" },
  { id: "hardness", title: "硬度换算计算器", desc: "HRC / HB / HV 金属硬度互相转换" },
  { id: "tension-check", title: "螺栓拉伸应力校核", desc: "螺栓静态拉伸载荷简易强度验算" },
  { id: "torque-unified", title: "英制 UNC/UNF 扭矩计算器", desc: "美标英制螺栓拧紧扭矩计算" },
  { id: "shear-check", title: "螺栓剪切强度校核", desc: "单剪切面螺栓抗剪安全系数验算" },
  { id: "min-engagement", title: "螺纹最小旋合长度", desc: "钢 / 铝 / 铸铁基体防螺纹滑牙计算" },
  { id: "bolt-length", title: "螺栓长度选型计算器", desc: "通孔带螺母场景快速选螺栓标准长度" },
  { id: "slip-factor", title: "连接面滑移安全系数", desc: "预紧摩擦力抵抗横向载荷滑移校核" },
];

export const CALCULATOR_LIST_EN: CalculatorMeta[] = [
  { id: "unit-converter", title: "Metric / Inch Unit Converter", desc: "Quick length conversion between inch and millimeters" },
  { id: "torque-metric", title: "Bolt Torque Calculator (Metric)", desc: "Assembly torque based on size and property class" },
  { id: "tap-drill", title: "Thread Tap Drill Size Calculator", desc: "Recommended drill size for coarse / fine metric taps" },
  { id: "hardness", title: "Hardness Converter", desc: "Convert between HRC, HB and HV for common steels" },
  { id: "tension-check", title: "Bolt Tension Stress Check", desc: "Simplified proof load and safety factor verification" },
  { id: "torque-unified", title: "UNC / UNF Bolt Torque Calculator", desc: "Installation torque for unified inch bolts" },
  { id: "shear-check", title: "Bolt Shear Strength Check", desc: "Single-plane shear safety factor calculation" },
  { id: "min-engagement", title: "Minimum Thread Engagement", desc: "Avoid thread stripping in steel / aluminium / cast iron" },
  { id: "bolt-length", title: "Bolt Length Selector", desc: "Pick a standard bolt length for through-hole + nut" },
  { id: "slip-factor", title: "Slip-Critical Joint Safety Factor", desc: "Preload friction vs lateral shear slip check" },
];

const VALID_IDS = new Set<string>(CALCULATOR_LIST.map((c) => c.id));

export function isValidCalculatorId(id: string): id is CalculatorId {
  return VALID_IDS.has(id);
}

export function getCalculatorList(lang: CalcLang = "zh"): CalculatorMeta[] {
  return lang === "en" ? CALCULATOR_LIST_EN : CALCULATOR_LIST;
}

export function getCalculatorMeta(id: CalculatorId, lang: CalcLang = "zh"): CalculatorMeta {
  const list = lang === "en" ? CALCULATOR_LIST_EN : CALCULATOR_LIST;
  return list.find((c) => c.id === id) ?? CALCULATOR_LIST.find((c) => c.id === id)!;
}

export const UI_STRINGS = {
  zh: {
    listTitle: "紧固件在线计算器",
    listSubtitle: "点击下方卡片，进入对应的独立计算器页面。",
    cardCta: "进入 →",
    backToList: "← 返回计算器列表",
    errorTitle: "计算器不存在",
    errorDesc: "没有找到 ID 为",
    errorBack: "返回计算器列表",
    errorAvailTitle: "可选计算器",
  },
  en: {
    listTitle: "Fastener Online Calculators",
    listSubtitle: "Click a card below to open the dedicated calculator page.",
    cardCta: "Open →",
    backToList: "← Back to Calculators",
    errorTitle: "Calculator not found",
    errorDesc: "No calculator with ID",
    errorBack: "Back to Calculator List",
    errorAvailTitle: "Available Calculators",
  },
} as const satisfies Record<CalcLang, Record<string, string>>;

// ========================================================================
// 计算器内部共享文案（表单 label / 下拉选项 / 结果 dt / 提示 note 等）
// ========================================================================

export const SHARED_STRINGS = {
  zh: {
    common: {
      size: "螺纹规格",
      boltSize: "螺栓规格",
      grade: "性能等级",
      shortSize: "规格",
      shortGrade: "等级",
      preloadRatio: "预紧比（屈服极限百分比）",
      preloadRatioShort: "预紧比例",
      lubCondition: "润滑条件（扭矩系数 K）",
      lubConditionShort: "润滑条件",
      lubDry: "干摩擦（K=0.20）",
      lubOil: "轻度润滑（K=0.15）",
      lubOil2: "轻润滑（K=0.15）",
      lubMoly: "二硫化钼（K=0.12）",
      pass: "✓ 通过",
      fail15: "✗ 不通过（建议 ≥1.5）",
      fail25: "✗ 不通过（建议 ≥2.5）",
      fail13: "✗ <1.3 不通过",
      pass13: "✓ ≥1.3 通过",
      invalid: "请输入有效数值",
      kN: "kN",
      percent: "%",
    },
    unit: {
      label: "换算方向",
      in2mm: "英寸 → 毫米",
      mm2in: "毫米 → 英寸",
      inputIn: "输入（英寸）",
      inputMm: "输入（毫米）",
      mm: "毫米 (mm)",
      cm: "厘米 (cm)",
      m: "米 (m)",
      um: "微米 (μm)",
      ft: "英尺 (ft)",
      in: "英寸 (in)",
      fracIn: "分数英寸",
    },
    torqueMetric: {
      note: "※ 扭矩仅为参考，实际装配应结合工艺试验、液压拉伸或应变片标定。",
      resArea: "应力截面积 As",
      resForce: "建议预紧力 Ff",
      resTorque: "建议装配扭矩",
      resStress: "预紧应力",
      resSafety: "屈服安全系数",
    },
    tapDrill: {
      labelBase: "基体材料",
      steel: "钢 / 合金钢",
      castiron: "铸铁（推荐稍大）",
      aluminum: "铝 / 铝合金（推荐更大）",
      plastic: "塑料（75% 即可）",
      pitch: "螺距 P",
      iso: "推荐底孔（ISO常规）",
      h75: "75% 螺纹高度",
      h85: "85% 螺纹高度",
      chamfer: "建议钻孔后倒角",
      blind: "最小盲孔深度",
      note: "※ 不锈钢、钛合金、高温合金建议选底孔偏大值，避免攻丝折断。",
      coarse: "粗牙",
      fine: "细牙",
    },
    hardness: {
      labelType: "输入硬度类型",
      labelValue: "输入值",
      hrc: "HRC 洛氏 C 标尺",
      hb: "HB 布氏硬度",
      hv: "HV 维氏硬度",
      hrcOut: "HRC 洛氏 C",
      hbOut: "HB 布氏",
      hvOut: "HV 维氏",
      uts: "近似抗拉强度 Rm",
      note: "※ 换算公式为近似经验值，针对碳钢调质范围有较好精度，不同材料需用对应对照表复核。",
    },
    tension: {
      load: "工作拉伸载荷",
      dynamic: "动载 / 冲击系数",
      totalLoad: "设计总载荷",
      sigma: "拉伸应力 σ",
      sfYield: "屈服安全系数",
      sfTensile: "抗拉安全系数",
    },
    torqueUnified: {
      strengthClass: "强度等级",
      resPreload: "预紧力",
      resImperial: "装配扭矩（英制）",
      resMetric: "装配扭矩（公制）",
    },
    shear: {
      planes: "剪切面数量",
      plane1: "单剪切面（如普通受剪连接）",
      plane2: "双剪切面（如铰制孔螺栓/双板连接）",
      load: "横向剪切载荷",
      totalArea: "总剪切面积",
      tau: "剪切应力 τ",
      sfY: "抗剪屈服系数",
      sfU: "抗剪极限系数",
      planesSuffix: "面",
      note: "※ 若剪切面通过螺纹，需改用螺纹小径计算；铰制孔螺栓建议按光杆核算。",
    },
    engagement: {
      boltGrade: "螺栓等级",
      baseMat: "被连接件材料（螺孔基体）",
      steel: "钢（Rp0.2≈240MPa）",
      castiron: "铸铁（Rp0.2≈160MPa）",
      aluminum: "铝合金（Rp0.2≈110MPa）",
      boltRp: "螺栓 Rp0.2",
      baseRp: "基体近似 Rp0.2",
      ratio: "强度比（螺栓/基体）",
      minTurns: "推荐最小旋合圈数",
      minLen: "推荐最小旋合长度",
      blind: "工程推荐盲孔深度",
      turns: "圈",
      mm: "mm",
      mmSuffix: "（含钻头锥尾余量）",
      note: "※ 螺纹滑牙多发生在软材料基体，建议在铝合金/铸铁上采用钢丝螺套或加大螺栓规格。",
    },
    boltLen: {
      stackup: "被连接件总厚度（板厚之和）",
      washers: "平垫圈数量",
      washer0: "无",
      washer1: "单侧 1 片（共 1）",
      washer2: "双侧各 1 片（共 2）",
      nutType: "螺母类型",
      nutStd: "普通六角薄/标准螺母",
      nutLock: "厚螺母 / 锁紧螺母（×1.5高）",
      chamfer: "末端倒角 + 伸出量",
      chamferNorm: "常规（2P 伸出 + 倒角）",
      chamferCon: "更保守（3P 伸出）",
      lmin: "计算最小长度 Lmin",
      std: "建议标准公称长度",
      exposure: "安装后螺栓末端伸出螺母",
      teeth: "牙",
      exposureOk: "✓ ≥1P 符合常规",
      exposureBad: "✗ 低于 1P，建议加长",
      nutH: "螺母高度 H",
      wash: "单片垫圈厚 s",
    },
    slip: {
      faying: "连接面处理",
      sb: "喷砂除锈（μ≈0.50）",
      ms: "保留氧化皮（μ≈0.45）",
      gd: "热浸镀锌（μ≈0.35）",
      pt: "富锌底漆/涂层（μ≈0.30）",
      count: "同排螺栓数量",
      total: "总横向剪切载荷",
      onePreload: "单颗预紧力 Ff",
      normal: "总正压力",
      countSuffix: "颗",
      friction: "摩擦抗剪力 μ·ΣFf",
      kf: "滑移安全系数",
      mu: "接触面摩擦系数 μ",
      note: "※ 摩擦型连接建议 Kf ≥ 1.30。若达不到，建议增加螺栓、提高等级、改用喷砂面或增大预紧。",
    },
  },
  en: {
    common: {
      size: "Thread size",
      boltSize: "Bolt size",
      grade: "Property class",
      shortSize: "Size",
      shortGrade: "Class",
      preloadRatio: "Preload ratio (% of yield)",
      preloadRatioShort: "Preload ratio",
      lubCondition: "Lubrication (torque factor K)",
      lubConditionShort: "Lubrication",
      lubDry: "Dry (K=0.20)",
      lubOil: "Lightly oiled (K=0.15)",
      lubOil2: "Lightly oiled (K=0.15)",
      lubMoly: "Molybdenum disulfide (K=0.12)",
      pass: "✓ Pass",
      fail15: "✗ Fail (≥1.5 recommended)",
      fail25: "✗ Fail (≥2.5 recommended)",
      fail13: "✗ Fail (Kf < 1.3)",
      pass13: "✓ Kf ≥ 1.3",
      invalid: "Please enter a valid value",
      kN: "kN",
      percent: "%",
    },
    unit: {
      label: "Conversion direction",
      in2mm: "Inch → Millimeter",
      mm2in: "Millimeter → Inch",
      inputIn: "Input (inch)",
      inputMm: "Input (mm)",
      mm: "Millimeter (mm)",
      cm: "Centimeter (cm)",
      m: "Meter (m)",
      um: "Micrometer (μm)",
      ft: "Foot (ft)",
      in: "Inch (in)",
      fracIn: "Fractional inch",
    },
    torqueMetric: {
      note: "※ Torque is indicative only. Real assembly should be validated by process trials, hydraulic tensioner, or strain-gauged bolts.",
      resArea: "Stress area As",
      resForce: "Recommended preload Ff",
      resTorque: "Installation torque",
      resStress: "Preload stress",
      resSafety: "Yield safety factor",
    },
    tapDrill: {
      labelBase: "Base material",
      steel: "Steel / Alloy steel",
      castiron: "Cast iron (slightly larger drill recommended)",
      aluminum: "Aluminium / Aluminium alloy (larger drill recommended)",
      plastic: "Plastic (75% thread height is enough)",
      pitch: "Pitch P",
      iso: "Recommended tap drill (ISO standard)",
      h75: "75% thread height",
      h85: "85% thread height",
      chamfer: "Chamfer after drilling",
      blind: "Minimum blind-hole depth",
      note: "※ For stainless steel, titanium or superalloys, use the larger recommended drill size to avoid tap breakage.",
      coarse: "coarse",
      fine: "fine",
    },
    hardness: {
      labelType: "Input hardness scale",
      labelValue: "Input value",
      hrc: "HRC (Rockwell C)",
      hb: "HB (Brinell)",
      hv: "HV (Vickers)",
      hrcOut: "HRC Rockwell C",
      hbOut: "HB Brinell",
      hvOut: "HV Vickers",
      uts: "Approx. tensile strength Rm",
      note: "※ Conversion is empirical and works best for carbon / alloy steels. Always cross-check with material conversion tables.",
    },
    tension: {
      load: "Applied tension load",
      dynamic: "Dynamic / impact factor",
      totalLoad: "Factored total load",
      sigma: "Tensile stress σ",
      sfYield: "Yield safety factor",
      sfTensile: "Ultimate safety factor",
    },
    torqueUnified: {
      strengthClass: "Strength grade",
      resPreload: "Preload force",
      resImperial: "Installation torque (imperial)",
      resMetric: "Installation torque (metric)",
    },
    shear: {
      planes: "Shear planes",
      plane1: "Single shear plane (common lap joint)",
      plane2: "Double shear planes (e.g. reamed bolt / double plate)",
      load: "Transverse shear load",
      totalArea: "Total shear area",
      tau: "Shear stress τ",
      sfY: "Shear yield safety factor",
      sfU: "Shear ultimate safety factor",
      planesSuffix: "planes",
      note: "※ If the shear plane passes through the threads, use the minor diameter instead. Reamed bolts should be calculated on shank diameter.",
    },
    engagement: {
      boltGrade: "Bolt grade",
      baseMat: "Joint material (tapped hole base)",
      steel: "Steel (Rp0.2≈240MPa)",
      castiron: "Cast iron (Rp0.2≈160MPa)",
      aluminum: "Aluminium alloy (Rp0.2≈110MPa)",
      boltRp: "Bolt Rp0.2",
      baseRp: "Base material approx. Rp0.2",
      ratio: "Strength ratio (bolt / base)",
      minTurns: "Min. recommended turns",
      minLen: "Min. recommended engagement length",
      blind: "Recommended blind-hole depth",
      turns: "turns",
      mm: "mm",
      mmSuffix: "(incl. drill tip allowance)",
      note: "※ Thread stripping is common in soft materials. Consider wire thread inserts or a larger bolt size for aluminium / cast iron.",
    },
    boltLen: {
      stackup: "Total clamped stack thickness",
      washers: "Flat washers count",
      washer0: "None",
      washer1: "One side only (total 1)",
      washer2: "Both sides (total 2)",
      nutType: "Nut type",
      nutStd: "Standard hex thin / regular nut",
      nutLock: "Thick nut / lock nut (×1.5 height)",
      chamfer: "End chamfer + protrusion",
      chamferNorm: "Normal (2P protrusion + chamfer)",
      chamferCon: "Conservative (3P protrusion)",
      lmin: "Calculated minimum length Lmin",
      std: "Recommended standard nominal length",
      exposure: "Bolt end protrusion beyond nut",
      teeth: "threads",
      exposureOk: "✓ ≥1P normal",
      exposureBad: "✗ < 1P, increase length",
      nutH: "Nut height H",
      wash: "Single washer thickness s",
    },
    slip: {
      faying: "Faying surface treatment",
      sb: "Blast-cleaned (μ≈0.50)",
      ms: "With mill scale (μ≈0.45)",
      gd: "Hot-dip galvanized (μ≈0.35)",
      pt: "Zinc-rich paint / coated (μ≈0.30)",
      count: "Bolts in the same row",
      total: "Total transverse shear load",
      onePreload: "Single bolt preload Ff",
      normal: "Total normal force",
      countSuffix: "bolts",
      friction: "Friction resistance μ·ΣFf",
      kf: "Slip-critical safety factor",
      mu: "Interface friction coefficient μ",
      note: "※ Kf ≥ 1.30 is recommended for slip-critical joints. Otherwise increase bolts, upgrade class, use blast-cleaned surfaces, or raise preload.",
    },
  },
} as const satisfies Record<CalcLang, Record<string, Record<string, string>>>;
